/** Academy billed with prepaid match credits instead of team subscriptions. */
export const CREDIT_BILLED_ACADEMY_ID = '8e4b7a8e-5e5a-4cd4-84cc-8d1d6af3267d';

export function usesAcademyCredits(academyId) {
  return String(academyId || '') === CREDIT_BILLED_ACADEMY_ID;
}

/** Pounds per 1 USD on 2026-10-07. Replaced by the live quote when that loads. */
const FALLBACK_GBP_PER_USD = 0.753989;

export function usdFromGbp(gbp, gbpPerUsd = FALLBACK_GBP_PER_USD) {
  const pounds = Number(gbp);
  const rate = Number(gbpPerUsd);
  if (!Number.isFinite(pounds) || pounds <= 0 || !Number.isFinite(rate) || rate <= 0) return null;
  return Math.round((pounds / rate) * 100) / 100;
}

/**
 * `priceGbp` is the price on screen. `price` is the USD equivalent sent to CyberSource.
 */
export const ACADEMY_CREDIT_PACKAGES = [
  { id: 'game_1', credits: 1, priceGbp: 35, perGameGbp: 35, price: usdFromGbp(35), perGame: usdFromGbp(35), label: 'Single Match', popular: false, savings: null },
  { id: 'game_10', credits: 10, priceGbp: 300, perGameGbp: 30, price: usdFromGbp(300), perGame: usdFromGbp(30), label: 'Pro Pack', popular: true, savings: 14 },
];

export function formatGbp(amount) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '';
  return Number.isInteger(value) ? `£${value}` : `£${value.toFixed(2)}`;
}

export function formatUsdCharge(amount) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '';
  return `$${value.toFixed(2)}`;
}

function applyQuote(packages, quote) {
  const rate = Number(quote?.gbpPerUsd);
  return packages.map((pkg) => {
    const row = (quote?.packages || []).find((item) => item.id === pkg.id);
    const price = row?.amountUsd != null
      ? Number(row.amountUsd)
      : usdFromGbp(pkg.priceGbp, rate || undefined);
    const perGame = row?.perGameUsd != null
      ? Number(row.perGameUsd)
      : usdFromGbp(pkg.perGameGbp, rate || undefined);
    return {
      ...pkg,
      price: Number.isFinite(price) ? price : pkg.price,
      perGame: Number.isFinite(perGame) ? perGame : pkg.perGame,
    };
  });
}

export async function loadAcademyCreditQuote() {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_KEY;
  if (supabaseUrl && supabaseAnonKey) {
    try {
      const response = await fetch(`${supabaseUrl}/functions/v1/academy-credit-quote`, {
        headers: { apikey: supabaseAnonKey },
      });
      const payload = await response.json().catch(() => ({}));
      if (response.ok && payload?.success && Array.isArray(payload.packages)) return payload;
    } catch {
      // Fall through to the public rate.
    }
  }
  try {
    const response = await fetch('https://open.er-api.com/v6/latest/USD');
    const payload = await response.json();
    const gbpPerUsd = Number(payload?.rates?.GBP);
    if (payload?.result === 'success' && gbpPerUsd > 0) {
      return {
        success: true,
        gbpPerUsd,
        packages: ACADEMY_CREDIT_PACKAGES.map((pkg) => ({
          id: pkg.id,
          amountUsd: usdFromGbp(pkg.priceGbp, gbpPerUsd),
          perGameUsd: usdFromGbp(pkg.perGameGbp, gbpPerUsd),
        })),
      };
    }
  } catch {
    // Keep the fallback amounts already on the packages.
  }
  return null;
}

export function withAcademyQuote(packages, quote) {
  if (!quote) return packages;
  return applyQuote(packages, quote);
}

export async function sendAcademyCreditPaymentLink({ academyId, packageId, teamId, email }) {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_KEY;
  if (!supabaseUrl || !supabaseAnonKey) throw new Error('Supabase is not configured.');

  const { default: supabase } = await import('../supabaseClient');
  const { data: { session } } = await supabase.auth.getSession();
  const accessToken = session?.access_token;
  if (!accessToken) throw new Error('You must be signed in to send a payment link.');

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const site = origin.startsWith('https://') && !/localhost|127\.0\.0\.1/i.test(origin)
    ? origin.replace(/\/$/, '')
    : 'https://www.statbox.co';

  let response;
  try {
    response = await fetch(`${supabaseUrl}/functions/v1/create-academy-credit-payment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: supabaseAnonKey,
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ academyId, packageId, teamId, email, origin: site }),
    });
  } catch (err) {
    const detail = err?.message || String(err);
    throw new Error(
      detail === 'Failed to fetch' || err instanceof TypeError
        ? 'Could not reach the payment service. Please try again.'
        : detail,
    );
  }

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload?.success) {
    throw new Error(payload?.error || 'Could not send the payment link.');
  }
  return payload;
}
